import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, Maximize2, Download } from "lucide-react";
import api, { API_BASE } from "../lib/api";

/**
 * Fullscreen modal that fetches and renders a single SVG with smooth
 * pan/zoom. For multi-drawing codes (iso / loop diagrams) the parent
 * passes a list of {id, label, src} entries; the viewer shows a side
 * rail to pick which drawing to display.
 */
export default function DrawingViewerModal({
  open,
  onClose,
  title,
  subtitle,
  // Single drawing: pass `src` (URL).
  src,
  // Multi-drawing: pass `items` = [{ id, label, src, downloadHref }]
  items,
  // Optional download URL for the currently-shown drawing.
  downloadHref,
}) {
  const [svgText, setSvgText] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [zoom, setZoom] = useState(1);
  const [tx, setTx] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const activeItem = items?.[activeIdx];
  const activeSrc = src || activeItem?.src;
  const activeDownload = downloadHref || activeItem?.downloadHref || activeSrc;

  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
    setZoom(1);
    setTx({ x: 0, y: 0 });
  }, [open, src]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !activeSrc) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    setZoom(1);
    setTx({ x: 0, y: 0 });
    api.get(activeSrc.replace(/^\/api/, ""), { responseType: "text" })
      .then((r) => {
        if (cancelled) return;
        let body = r.data;
        if (typeof body !== "string") body = JSON.stringify(body);
        setSvgText(body);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, activeSrc]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setZoom((z) => Math.max(0.2, Math.min(8, z + delta * z)));
  };

  const onMouseDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, ox: tx.x, oy: tx.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setTx({ x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) });
  };
  const onMouseUp = () => { dragRef.current = null; };

  const fit = () => { setZoom(1); setTx({ x: 0, y: 0 }); };

  const filteredItems = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.label || it.id || "").toLowerCase().includes(q));
  }, [items, search]);

  if (!open) return null;
  return (
    <div
      data-testid="drawing-viewer-modal"
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(2, 6, 23, 0.94)",
        backdropFilter: "blur(6px)",
        display: "flex", flexDirection: "column",
        color: "#F3F4F6",
        fontFamily: "ui-sans-serif, system-ui, -apple-system",
      }}
    >
      <header style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 18px",
        borderBottom: "1px solid #1F2937",
        background: "rgba(15, 23, 42, 0.85)",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "#FFB000",
                            letterSpacing: 1, textTransform: "uppercase" }}>
            {title || "Drawing"}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600,
                            color: "#F3F4F6",
                            whiteSpace: "nowrap", overflow: "hidden",
                            textOverflow: "ellipsis" }}>
            {subtitle || activeItem?.label || ""}
          </div>
        </div>
        <button
          onClick={() => setZoom((z) => Math.max(0.2, z / 1.25))}
          data-testid="viewer-zoom-out"
          title="Zoom out"
          style={btnStyle()}
        >
          <ZoomOut size={14} />
        </button>
        <span style={{ minWidth: 56, textAlign: "center",
                          fontFamily: "ui-monospace", color: "#D1D5DB",
                          fontSize: 12 }}>
          {(zoom * 100).toFixed(0)}%
        </span>
        <button
          onClick={() => setZoom((z) => Math.min(8, z * 1.25))}
          data-testid="viewer-zoom-in"
          title="Zoom in"
          style={btnStyle()}
        >
          <ZoomIn size={14} />
        </button>
        <button
          onClick={fit}
          data-testid="viewer-fit"
          title="Reset view"
          style={btnStyle()}
        >
          <Maximize2 size={14} />
        </button>
        {activeDownload && (
          <a
            href={`${API_BASE}${activeDownload}`}
            data-testid="viewer-download"
            title="Download"
            style={{ ...btnStyle(), color: "#FFB000",
                       borderColor: "#FFB000", textDecoration: "none" }}
          >
            <Download size={14} />
          </a>
        )}
        <button
          onClick={onClose}
          data-testid="viewer-close"
          title="Close (Esc)"
          style={{ ...btnStyle(),
                       borderColor: "#7F1D1D", color: "#FCA5A5" }}
        >
          <X size={14} />
        </button>
      </header>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {items && items.length > 0 && (
          <aside data-testid="viewer-side-rail" style={{
            width: 270, borderRight: "1px solid #1F2937",
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex", flexDirection: "column",
          }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${items.length} drawings…`}
              data-testid="viewer-search"
              style={{
                margin: 10,
                background: "#0B1220", color: "#F3F4F6",
                border: "1px solid #374151",
                padding: "7px 10px", borderRadius: 6, fontSize: 12,
              }}
            />
            <div style={{ overflowY: "auto", flex: 1 }}>
              {filteredItems.map((it) => {
                const idx = items.indexOf(it);
                const active = idx === activeIdx;
                return (
                  <button
                    key={it.id}
                    onClick={() => setActiveIdx(idx)}
                    data-testid={`viewer-item-${it.id}`}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "8px 12px",
                      background: active ? "#0B1220" : "transparent",
                      borderLeft: active
                        ? "3px solid #FFB000"
                        : "3px solid transparent",
                      border: "none", borderTop: "1px solid #1F2937",
                      color: active ? "#FFB000" : "#D1D5DB",
                      fontSize: 12, cursor: "pointer",
                      fontFamily: "ui-monospace",
                    }}
                  >
                    {it.label || it.id}
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        <main
          ref={containerRef}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          style={{
            flex: 1, position: "relative", overflow: "hidden",
            cursor: dragRef.current ? "grabbing" : "grab",
            background:
              "radial-gradient(circle at 50% 50%, #0F172A 0%, #020617 100%)",
          }}
        >
          {loading && (
            <div style={center()}>
              <div style={{ fontSize: 13, color: "#9CA3AF" }}>
                Loading drawing…
              </div>
            </div>
          )}
          {err && (
            <div style={{ ...center(), color: "#FCA5A5", fontSize: 13 }}>
              {err}
            </div>
          )}
          {!loading && !err && (
            <div
              data-testid="viewer-svg-canvas"
              style={{
                position: "absolute", left: "50%", top: "50%",
                transform: `translate(calc(-50% + ${tx.x}px), calc(-50% + ${tx.y}px)) scale(${zoom})`,
                transformOrigin: "center center",
                transition: dragRef.current
                  ? "none"
                  : "transform 0.08s ease-out",
                userSelect: "none",
                pointerEvents: "none",
                background: "white",
                padding: 8, borderRadius: 6,
                boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
              }}
              dangerouslySetInnerHTML={{ __html: svgText }}
            />
          )}
        </main>
      </div>

      <footer style={{
        padding: "6px 16px",
        borderTop: "1px solid #1F2937",
        background: "rgba(15, 23, 42, 0.85)",
        display: "flex", gap: 18, alignItems: "center",
        fontSize: 11, color: "#6B7280",
      }}>
        <span>Scroll to zoom · Drag to pan · Esc closes</span>
        {items && (
          <span style={{ marginLeft: "auto" }}>
            {activeIdx + 1} / {items.length} drawings
          </span>
        )}
      </footer>
    </div>
  );
}

function btnStyle() {
  return {
    background: "transparent",
    border: "1px solid #374151",
    color: "#D1D5DB",
    padding: "6px 9px", borderRadius: 6,
    cursor: "pointer", display: "inline-flex",
    alignItems: "center", justifyContent: "center",
  };
}
function center() {
  return {
    position: "absolute", left: 0, right: 0, top: 0, bottom: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  };
}
